import { cva } from "class-variance-authority";
import * as React from "react";

import { cn } from "../../../lib/utils";

export type AvatarSize = 16 | 20 | 24 | 28 | 32 | 40 | 48 | 120;

type CommonAvatarProps = {
	/**
	 * Size of the avatar
	 */
	size?: AvatarSize;
	/**
	 * The id of the user to render the avatar for. If omitted, shows anonymous avatar.
	 */
	id?: string;
	/**
	 * Border around the avatar
	 */
	border?: boolean;
	/**
	 * Classname to override the default styles.
	 * ⚠️ Use sparingly - prefer using the `size` and `border` props.
	 */
	dangerouslySetClassName?: string;
	/**
	 * Custom URL for the avatar image. Overrides the default avatar URL generation.
	 * Useful for external users or custom avatar sources.
	 */
	url?: string;
	/**
	 * Test ID for testing purposes. Will be added as data-testid attribute.
	 */
	testId?: string;
	/**
	 * Title attribute for hover text. Provides additional context on hover.
	 */
	title?: string;
};

/**
 * Props for the Avatar component.
 *
 * Requires either `name` or `alt` to be provided for accessibility:
 *
 * - **`name`** (common case): The person's name. Alt text is auto-generated as `"[Name]'s avatar"`.
 *   You can still pass `alt` to override the generated text.
 *
 * - **`alt`** (special cases): Use for redacted users (`alt="Redacted user avatar"`),
 *   anonymous users (`alt="Anonymous employee avatar"`), or decorative avatars (`alt=""`).
 */
export type AvatarProps = CommonAvatarProps &
	(
		| {
				/**
				 * The person's name. Used to auto-generate alt text as `"[Name]'s avatar"`.
				 * You can still pass `alt` to override the generated text.
				 */
				name: string;
				/**
				 * Optional override for the auto-generated alt text.
				 * If omitted, alt is computed as `"[name]'s avatar"`.
				 *
				 * @see https://www.w3.org/WAI/tutorials/images/informative/
				 */
				alt?: string;
		  }
		| {
				name?: undefined;
				/**
				 * The alt attribute for the avatar image. Required when `name` is not provided.
				 *
				 * **Accessibility Guidelines**:
				 * - Use for special cases: redacted, anonymous, or decorative avatars
				 * - Use empty string "" ONLY if the name appears as visible text next to the avatar
				 *
				 * **Examples**:
				 * ```tsx
				 * <Avatar alt="Redacted user avatar" />
				 * <Avatar alt="Anonymous employee avatar" />
				 * <Avatar alt="" /> // decorative
				 * ```
				 *
				 * @see https://www.w3.org/WAI/tutorials/images/informative/
				 */
				alt: string;
		  }
	);

const getAvatarURL = (id: string) => {
	const mediaCDN = `${
		window.PERFORMYARD_MEDIA_CDN ? window.PERFORMYARD_MEDIA_CDN : "https://performyard-content.performyard.com/"
	}`;

	return `${mediaCDN}avatar/${id}/`;
};

/**
 * Component for rendering a user's avatar image. If no id is provided, a default anonymous avatar will be rendered.
 */
const avatarVariants = cva("size-5 rounded-full object-cover", {
	variants: {
		size: {
			16: "size-4",
			20: "size-5",
			24: "size-6",
			28: "size-7",
			32: "size-8",
			40: "size-10",
			48: "size-12",
			120: "size-[120px]",
		},
		border: {
			true: "border border-solid border-neutral-10",
		},
	},
	defaultVariants: {
		size: 40,
		border: false,
	},
});

export const Avatar = (props: AvatarProps) => {
	const { size = 40, id = "", dangerouslySetClassName, border, url, testId, title } = props;

	const anonymousAvatarURL = "https://dev-media.performyard.com/static/img/anonymous.png";

	// Determine image source
	let src = "";
	if (url) {
		// Custom URL takes precedence
		src = url;
	} else if (!id) {
		src = anonymousAvatarURL;
	} else {
		src = getAvatarURL(id);
	}

	// Compute alt text based on props
	let computedAlt: string;
	if (props.alt !== undefined) {
		// Explicit alt always wins (even when name is also provided)
		computedAlt = props.alt;
	} else if (props.name) {
		// Auto-generate from name
		computedAlt = `${props.name}'s avatar`;
	} else {
		// TypeScript enforces that one of name/alt is provided, but
		// handle the fallback defensively for JS callers
		computedAlt = "User avatar";
	}

	return (
		<img
			className={cn(avatarVariants({ size, border }), dangerouslySetClassName)}
			alt={computedAlt}
			src={src}
			title={title}
			data-testid={testId}
		/>
	);
};
